'use client';
import React, { useEffect, useRef, useState } from 'react';
import WritingComponent from '@/app/components/Writing';
import { PostTypeConfig } from '@/app/constants/typedef';
import usePostDetails from '@/app/hooks/useFeedDetails';
import { PostModel } from '@/app/models/posts/Posts';
import { notFound } from 'next/navigation';
import { IGroup } from '@/app/models/group/IGroup';
import { writingService } from '@/app/service/writingService';
import utilsService from '@/app/helpers/utilsService';
import { Dialog, Box, CircularProgress } from '@mui/material';

interface EditPostModal {
    id: string;
    onClose?: () => void;
    updatePostDetail?: (post: PostModel) => void;
    type?: PostTypeConfig;
}

const EditPostModal: React.FC<EditPostModal> = ({ id, onClose, updatePostDetail, type = PostTypeConfig.Posts }) => {
    const { isLoading, data, error } = usePostDetails<PostModel>(id as string, type);

    const fetchController = useRef<AbortController | null>(null);
    const [loadingGroup, setLoadingGroup] = useState(true);
    const [groups, setGroup] = useState<IGroup[]>([]);
    const [open, setOpen] = useState(true);

    // Memoize the data object to pass to WritingComponent
    const editData = React.useMemo(() => {
        if (!data) return undefined;

        return {
            caption: data.caption as string,
            file: data.media?.find((m) => m.mediaType == 'file'),
            media: data.media
                ?.filter((m) => m.mediaType !== 'file')
                .map((s, index) => ({
                    id: s.id,
                    mediaUrl: s.mediaUrl,
                    order: index + 1,
                    mediaType: s.mediaType,
                })) ?? [],
            groupId: data.group?.id?.toString() ?? 'all',
            isPublic: data.isPublic,
            // Product specific fields
            productName: data.name ?? undefined,
            price: data.price?.toString() ?? '',
            tags: data.tags?.map(t => t.name) ?? [],
            boardType: (data.postType === 'product' ? 'market' : 'board') as 'market' | 'board',
        };
    }, [data, type]);

    const fetchGroup = async (): Promise<void> => {
        try {
            if (fetchController.current) {
                fetchController.current.abort();
            }
            fetchController.current = new AbortController();
            const signal = fetchController.current.signal;
            const response = await writingService.getGroup(signal);
            setGroup(response?.data ?? []);
            setLoadingGroup(false);
        } catch (err) {
            const errorMessage = utilsService.getErrorMessage(err);
            if (errorMessage !== 'canceled') {
                setLoadingGroup(false);
            }
        }
    };

    useEffect(() => {
        fetchGroup();
    }, []);

    useEffect(() => {
        // Reset scroll when modal closes
        return () => {
            document.body.style.overflow = 'auto';
            document.body.style.removeProperty('overflow');
        };
    }, []);

    const handleClose = () => {
        setOpen(false);
        onClose?.();
    };

    if (error && !isLoading) {
        notFound();
    }

    if (isLoading || loadingGroup) {
        return (
            <Dialog
                open={open}
                fullScreen
                PaperProps={{
                    sx: {
                        backgroundColor: 'background.default',
                    }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <CircularProgress />
                </Box>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            fullScreen
            onClose={handleClose}
            PaperProps={{
                sx: {
                    backgroundColor: 'background.default',
                    margin: 0,
                    maxHeight: '100%',
                }
            }}
        >
            <WritingComponent
                groups={groups}
                loadingGroups={loadingGroup}
                updatePostDetail={updatePostDetail}
                onClose={handleClose}
                data={editData}
                id={data?.id?.toString()}
            />
        </Dialog>
    );
};

export default EditPostModal;
